/** Profile, password, theme, the weekly email, and connected apps. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ThemeToggle } from "../../components/layout/ThemeToggle";
import { ApiError, api, type Me, type Schemas } from "../../lib/api";
import { PushSection } from "./PushSettings";
import { useSession } from "./session";

export function SettingsPage() {
  const { user, logout } = useSession();
  const navigate = useNavigate();
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resent, setResent] = useState(false);

  const resend = useMutation({
    mutationFn: () => api.post("/auth/verify/resend"),
    onSuccess: () => setResent(true),
    onError: (cause) =>
      setError(cause instanceof ApiError ? cause.message : "Could not send that email."),
  });

  const change = useMutation({
    mutationFn: (input: { current_password: string; new_password: string }) =>
      api.post("/auth/change-password", input),
    onSuccess: () => {
      setMessage("Password changed. Other devices have been signed out.");
      setError(null);
    },
    onError: (cause) =>
      setError(cause instanceof ApiError ? cause.message : "Could not change the password."),
  });

  const hasPassword = user?.has_password ?? true;

  const queryClient = useQueryClient();
  const methods = useQuery({
    queryKey: ["auth-methods"],
    queryFn: () => api.get<{ weekly_digest?: boolean }>("/auth/methods"),
  });
  const digest = useMutation({
    mutationFn: (on: boolean) => api.patch<Me>("/auth/preferences", { weekly_digest: on }),
    onSuccess: (me) => queryClient.setQueryData(["me"], me),
  });
  // Offered when mail really goes out — and always to someone already opted in, so a
  // server whose mail stopped working still lets them turn it off.
  const showDigest = Boolean(methods.data?.weekly_digest || user?.weekly_digest);

  const connections = useQuery({
    queryKey: ["connected-apps"],
    queryFn: () => api.get<Schemas["GrantResponse"][]>("/connected-apps"),
  });
  const disconnect = useMutation({
    mutationFn: (id: number) => api.delete(`/connected-apps/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["connected-apps"] }),
  });

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    change.mutate({
      current_password: String(form.get("current") ?? ""),
      new_password: String(form.get("next")),
    });
    event.currentTarget.reset();
  }

  return (
    <main className="mx-auto w-full max-w-md px-5 py-8">
      <div className="mb-6 flex items-baseline justify-between">
        <h1 className="display text-lg">Settings</h1>
        <Link to="/" className="text-sm text-accent underline underline-offset-2">
          Back
        </Link>
      </div>

      <section className="mb-8">
        <h2 className="mb-2 text-2xs uppercase tracking-widest text-muted">Account</h2>
        <p className="text-sm">{user?.name}</p>
        <p className="text-sm text-muted">{user?.email}</p>
        {user?.has_google ? (
          <p className="mt-1 text-xs text-muted">Signed in with Google.</p>
        ) : null}

        {user && !user.email_verified ? (
          <div className="mt-3 rounded-lg border border-owing/35 bg-owing/[0.08] px-3 py-2.5 text-sm">
            <p className="mb-2">
              This address is not confirmed yet. Until it is, any group you have been
              invited to stays out of reach — confirming is what proves the invitation was
              meant for you.
            </p>
            <Button
              disabled={resend.isPending || resent}
              onClick={() => resend.mutate()}
            >
              {resent ? "Sent — check your email" : resend.isPending ? "Sending…" : "Send it again"}
            </Button>
          </div>
        ) : null}
      </section>

      {showDigest ? (
        <section className="mb-8">
          <h2 className="mb-2 text-2xs uppercase tracking-widest text-muted">
            Weekly summary
          </h2>
          <label className="flex cursor-pointer items-start gap-3 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 accent-[rgb(var(--accent))]"
              checked={user?.weekly_digest ?? false}
              disabled={digest.isPending}
              onChange={(event) => digest.mutate(event.target.checked)}
            />
            <span>
              Email me once a week when other people have added entries that include me —
              what each one put on my tab, and where I stand. Nothing is sent in a quiet
              week.
            </span>
          </label>
          {digest.isError ? (
            <p role="alert" className="mt-2 text-sm text-owing">
              Could not save that. Try again.
            </p>
          ) : null}
        </section>
      ) : null}

      <PushSection />

      {connections.data && connections.data.length > 0 ? (
        <section className="mb-8">
          <h2 className="mb-2 text-2xs uppercase tracking-widest text-muted">
            Connected apps
          </h2>
          <ul className="flex flex-col gap-2">
            {connections.data.map((grant) => (
              <li
                key={grant.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-rule px-3 py-2.5"
              >
                <div className="min-w-0 text-sm">
                  <p className="font-medium">{grant.name}</p>
                  <p className="truncate text-xs text-muted">
                    Can read {grant.groups.map((group) => group.name).join(", ")}
                  </p>
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={disconnect.isPending}
                  onClick={() => disconnect.mutate(grant.id)}
                >
                  Disconnect
                </Button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="mb-8">
        <h2 className="mb-2 text-2xs uppercase tracking-widest text-muted">Theme</h2>
        <ThemeToggle />
      </section>

      <section className="mb-8">
        <h2 className="mb-2 text-2xs uppercase tracking-widest text-muted">
          {hasPassword ? "Password" : "Set a password"}
        </h2>
        {!hasPassword ? (
          <p className="mb-2 text-sm text-muted">
            This account signs in with Google. Adding a password lets you sign in either
            way.
          </p>
        ) : null}
        {message ? <p className="mb-2 text-sm text-owed">{message}</p> : null}
        {error ? (
          <p role="alert" className="mb-2 text-sm text-owing">
            {error}
          </p>
        ) : null}
        <form className="flex flex-col gap-2" onSubmit={onSubmit}>
          {/* A Google-only account has never had a password, so there is nothing to
              prove here — the live session is the proof, and asking for one they were
              never issued would simply leave them unable to add one. */}
          {hasPassword ? (
            <Input
              name="current"
              type="password"
              placeholder="Current password"
              aria-label="Current password"
              autoComplete="current-password"
              required
            />
          ) : null}
          <Input
            name="next"
            type="password"
            placeholder="New password"
            aria-label="New password"
            autoComplete="new-password"
            minLength={10}
            required
          />
          <Button type="submit" variant="primary" disabled={change.isPending}>
            {hasPassword ? "Change password" : "Set password"}
          </Button>
        </form>
      </section>

      <Button variant="danger" onClick={() => void logout().then(() => navigate("/login"))}>
        Sign out
      </Button>
    </main>
  );
}
